import NumericInputField from 'components/common/NumericInputField';
import { NUTRITION_EDIT_RATE_LABEL } from 'constants/uiConstants';
import React from 'react';
import { LuLock } from 'react-icons/lu';
import type { NutritionProductLine } from './computeNutritionAudit';
import { focusAdjacentRate, NUTRITION_RATE_ATTR } from './focusAdjacentRate';
import IconTip from './IconTip';
import { formatRate } from './nutritionFormat';
import type { NutritionProgramEdit } from './useNutritionProgramEdit';

interface ProductRateProps {
  line: NutritionProductLine;
  edit: NutritionProgramEdit;
}

// One fixed box for every state — view, editing and locked — so a rate never
// changes size or shifts its row when it's clicked or committed. The visible
// border and white fill say "this is a field" before the user hovers.
// Right-aligned in both states (nutrition-rate-field in App.css aligns the
// input), so the number doesn't jump sideways when editing starts or ends.
const FIELD_BOX =
  'nutrition-rate-field flex h-8 w-28 items-center rounded border text-right text-base tabular-nums';
const UNIT_CLASS = 'w-16 flex-shrink-0 text-left text-sm text-gray-muted';

const rateUnitLabel = (line: NutritionProductLine) =>
  line.rateUnit ? `${line.rateUnit}/ha` : '';

/**
 * A product line's rate — plain text in Actual mode, an editable field in
 * Plan mode, or a filled, padlocked box with the reason when that plan can't
 * be changed from here.
 */
const ProductRate: React.FC<ProductRateProps> = ({ line, edit }) => {
  if (!edit.canEdit) return <>{formatRate(line.rate, line.rateUnit)}</>;

  const reason = edit.lockReason(line);
  if (reason) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          className={`${FIELD_BOX} justify-between border-gray-bright bg-secondary-light px-2 text-gray-dark`}
        >
          {line.rate}
          <IconTip title={reason}>
            <LuLock size={12} aria-hidden />
          </IconTip>
        </span>
        <span className={UNIT_CLASS}>{rateUnitLabel(line)}</span>
      </span>
    );
  }

  const saving = edit.isSaving(line);
  return (
    <span
      className="inline-flex items-center gap-1.5"
      {...{ [NUTRITION_RATE_ATTR]: '' }}
      aria-busy={saving || undefined}
      aria-label={NUTRITION_EDIT_RATE_LABEL(line.productName)}
    >
      {/* The box carries the border; the cell's own border is removed so
          hover and focus show one outline, not two. */}
      <span
        className={`${FIELD_BOX} border-gray-medium bg-white text-charcoal hover:border-success focus-within:border-success-dark`}
      >
        <NumericInputField
          className="h-full w-full"
          // Rounded to sit inside the box's rounded border: the field turns
          // white while editing, and square corners would paint over the
          // border's curve (see .nutrition-rate-field in App.css).
          inputClassName="h-full w-full rounded-[0.1875rem] border-0"
          value={edit.displayRate(line)}
          min={0}
          inclusiveMin
          disabled={saving}
          onArrowNavigate={focusAdjacentRate}
          onCommit={(value) => {
            if (value != null) edit.commitRate(line, value);
          }}
        />
      </span>
      <span className={UNIT_CLASS}>{rateUnitLabel(line)}</span>
    </span>
  );
};

export default ProductRate;
